import { Controller } from "@hotwired/stimulus"
import { NavigationMixin } from "./product-form/navigation"
import { FieldsMixin } from "./product-form/fields"
import { LocationMixin } from "./product-form/location"
import { createLogger } from "../lib/logger"

const log = createLogger('ProductForm')

// Product Form Controller - 상품 등록/수정 폼 (모듈화된 구조)
// 559줄 → 90줄로 축소 (메서드는 모듈로 분리)
export default class extends Controller {
  static targets = [
    "step",
    "cancelButton",
    "prevButton",
    "nextButton",
    "submitButton",
    "descriptionCount",
    "priceInput",
    "freeToggle",
    "freeOverlay",
    "addressField",
    "locationStatus",
    "latitudeDisplay",
    "longitudeDisplay",
    "latitudeField",
    "longitudeField"
  ]

  static values = {
    currentStep: Number,
    prohibitedErrorMessage: String,
    fixFieldsErrorMessage: String
  }

  connect() {
    // 초기화
    this.previousPrice = null

    // 모듈 메서드 바인딩 (this 컨텍스트 유지)
    this.bindMixins()

    // 초기 설정
    this.updateUI()
    this.updateDescriptionCount()
    this.syncFreeToggle()
    this.initializeNegotiableChip()
    this.syncTradeOptions()
    this.syncLocationDisplay()

    // 이벤트 리스너 등록
    this.setupEventListeners()

    // Turbo morphing 후 재초기화 (validation error 후 버튼 visibility 복원)
    this.handleTurboMorph = this.handleTurboMorph.bind(this)
    document.addEventListener("turbo:morph", this.handleTurboMorph)

    // Turbo 폼 제출 완료 후 버튼 복원 (validation 실패 시 버튼 텍스트 복원)
    this.handleTurboSubmitEnd = this.handleTurboSubmitEnd.bind(this)
    this.element.addEventListener("turbo:submit-end", this.handleTurboSubmitEnd)

    // 원본 버튼 텍스트 저장 (중복 제출 방지용)
    // data 속성 우선, 없으면 현재 텍스트 사용 (Turbo morph 후에도 안전)
    if (this.hasSubmitButtonTarget) {
      this.originalButtonText = this.submitButtonTarget.dataset.originalText
        || this.submitButtonTarget.textContent.trim()
    }
  }

  disconnect() {
    // 이벤트 리스너 정리
    this.element.removeEventListener("map-picker:coordinate-changed", this.handleMapCoordinateChange)
    this.element.removeEventListener("map-picker:address-resolved", this.handleAddressResolved)
    this.element.removeEventListener("address-autocomplete:place-selected", this.handleAutocompleteSelection)
    document.removeEventListener("turbo:morph", this.handleTurboMorph)
    this.element.removeEventListener("turbo:submit-end", this.handleTurboSubmitEnd)
  }

  // ⭐ 모듈 메서드를 컨트롤러 인스턴스에 바인딩
  bindMixins() {
    // Navigation 모듈
    Object.keys(NavigationMixin).forEach(key => {
      this[key] = NavigationMixin[key].bind(this)
    })

    // Fields 모듈
    Object.keys(FieldsMixin).forEach(key => {
      this[key] = FieldsMixin[key].bind(this)
    })

    // Location 모듈
    Object.keys(LocationMixin).forEach(key => {
      this[key] = LocationMixin[key].bind(this)
    })
  }

  // 이벤트 리스너 설정
  setupEventListeners() {
    // map-picker 컨트롤러로부터 좌표 업데이트 수신
    this.element.addEventListener("map-picker:coordinate-changed", this.handleMapCoordinateChange.bind(this))

    // map-picker 컨트롤러로부터 지오코딩된 주소 수신
    this.element.addEventListener("map-picker:address-resolved", this.handleAddressResolved.bind(this))

    // address-autocomplete 컨트롤러로부터 장소 선택 수신
    this.element.addEventListener("address-autocomplete:place-selected", this.handleAutocompleteSelection.bind(this))

    // prohibited-keywords 컨트롤러로부터 validation 상태 수신
    this.element.addEventListener("prohibited-keywords:validation-changed", this.handleValidationChange.bind(this))
  }

  // Handle validation state changes from prohibited-keywords controller
  // Disables/enables submit button based on prohibited keywords detection
  handleValidationChange(event) {
    const { isValid } = event.detail
    const submitButton = this.hasSubmitButtonTarget ? this.submitButtonTarget : null

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', !isValid)

      // Add visual feedback for disabled state
      if (!isValid) {
        submitButton.classList.add("opacity-50", "cursor-not-allowed")
        submitButton.classList.remove("hover:bg-crust-700")
      } else {
        submitButton.classList.remove("opacity-50", "cursor-not-allowed")
        submitButton.classList.add("hover:bg-crust-700")
      }

      log.debug(`Submit button ${isValid ? 'enabled' : 'disabled'} (prohibited keywords: ${!isValid})`)
    }
  }

  // Handle Turbo morphing to re-initialize button visibility
  // Ensures navigation buttons remain visible after validation errors
  handleTurboMorph() {
    log.debug("Turbo morph detected, re-initializing UI")
    this.updateUI()
    // Turbo morph 후 버튼 텍스트 복원 (idiomorph가 보존한 텍스트 덮어쓰기)
    this.restoreSubmitButton()
  }

  // Turbo 폼 제출 완료 시 버튼 복원 (성공/실패 무관)
  handleTurboSubmitEnd(event) {
    this.restoreSubmitButton()
    log.debug("Turbo submit ended, button restored")
  }

  // Form submission validation
  async handleSubmit(event) {
    // 중복 제출 방지: 이미 제출 중이면 차단
    if (this.hasSubmitButtonTarget && this.submitButtonTarget.disabled) {
      event.preventDefault()
      event.stopPropagation()
      return false
    }

    // 즉시 버튼 비활성화 (Turbo보다 먼저 실행됨)
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
      const submittingText = this.submitButtonTarget.dataset.turboSubmitsWith
      if (submittingText) {
        this.submitButtonTarget.textContent = submittingText
      }
    }

    // aria-disabled 체크 (validation error 상태)
    if (this.hasSubmitButtonTarget && this.submitButtonTarget.getAttribute('aria-disabled') === 'true') {
      // 버튼 재활성화 (validation 실패 시)
      this.restoreSubmitButton()
      event.preventDefault()
      event.stopPropagation()
      await this.handleDisabledSubmitClick(event)
      return false
    }

    // Import platform toast helper dynamically
    const { showError } = await import("../lib/platform_toast")

    // Check if prohibited keywords validation controller exists
    const prohibitedController = this.element.querySelector('[data-controller*="prohibited-keywords"]')

    if (prohibitedController) {
      const controller = this.application.getControllerForElementAndIdentifier(
        prohibitedController,
        "prohibited-keywords"
      )

      if (controller && !controller.isValid()) {
        // 버튼 재활성화 (validation 실패 시)
        this.restoreSubmitButton()
        event.preventDefault()
        event.stopPropagation()

        // Show error toast using I18n message from data attribute (no fallback hardcoding)
        const errorMessage = this.prohibitedErrorMessageValue
        await showError(errorMessage)
        log.warn("Form submission blocked: prohibited keywords detected")
        return false
      }
    }

    return true
  }

  // 헬퍼: 버튼 복원 (validation 실패 시)
  restoreSubmitButton() {
    if (this.hasSubmitButtonTarget && this.originalButtonText) {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = this.originalButtonText
    }
  }

  // Handle disabled submit button clicks for better UX
  // Shows helpful toast message and scrolls to first error field
  async handleDisabledSubmitClick(event) {
    const submitButton = this.hasSubmitButtonTarget ? this.submitButtonTarget : null

    // Only handle clicks when button is disabled
    if (submitButton && submitButton.getAttribute('aria-disabled') === 'true') {
      event.preventDefault()
      event.stopPropagation()

      // Show helpful toast message using I18n from data attribute
      const { showError } = await import("../lib/platform_toast")
      await showError(this.fixFieldsErrorMessageValue)

      log.debug("Disabled submit button clicked - showing guidance")

      // Navigate to error field's step, then scroll
      const errorField = this.element.querySelector('.border-red-500')

      if (errorField) {
        // 1. Detect which step contains the error
        const errorStepNumber = this.getStepNumberForElement(errorField)

        // 2. Navigate to error's step if different from current step
        if (errorStepNumber && errorStepNumber !== this.currentStepValue) {
          log.debug(`Navigating to step ${errorStepNumber} (error detected in different step)`)
          this.currentStepValue = errorStepNumber
          this.updateUI() // Removes 'hidden' class from error's step

          // 3. Wait for step transition + animation complete
          // CRITICAL: updateUI() calls scrollToSection() which starts smooth scroll animation
          // Must wait for that animation to finish BEFORE calling scrollToElement()
          // Otherwise: double scroll animation (jarring UX + Android keyboard timing conflict)
          await new Promise(resolve => setTimeout(resolve, 400)) // 300ms button animation + 100ms buffer
        }

        // 4. Scroll to error field (now guaranteed to be visible)
        await this.scrollToErrorField(errorField)
      }
    }
  }

  // Helper: Detect which step contains an element
  getStepNumberForElement(element) {
    const stepSection = element.closest('[data-step]')
    return stepSection ? parseInt(stepSection.dataset.step, 10) : null
  }

  // Helper: Scroll to error field using scroll-focus controller
  async scrollToErrorField(errorField) {
    // Use scroll-focus controller's header-aware scrolling
    const scrollFocusController = this.application
      .getControllerForElementAndIdentifier(this.element, 'scroll-focus')

    if (scrollFocusController) {
      // Delegate to scroll-focus for proper header offset
      scrollFocusController.scrollToElement(errorField)

      // Focus after scroll completes (scroll-focus uses 350ms * 2 = 700ms total)
      setTimeout(() => errorField.focus(), 750)
    } else {
      // Fallback: manual header offset compensation
      errorField.scrollIntoView({ behavior: 'smooth', block: 'start' })

      setTimeout(() => {
        // Compensate for fixed header (80px base + 20px buffer + 40px adjustment = 140px)
        const headerOffset = 140
        const rect = errorField.getBoundingClientRect()

        if (rect.top < headerOffset) {
          window.scrollBy({
            top: rect.top - headerOffset,
            behavior: 'smooth'
          })
        }

        // Focus after adjustment
        errorField.focus()
      }, 400) // 350ms scroll + 50ms buffer
    }
  }

  // 📌 모듈에서 제공하는 메서드들:
  //
  // === Navigation (product-form/navigation.js) ===
  // - nextStep()
  // - prevStep()
  // - updateUI()
  // - toggleButtonVisibility(button, shouldShow)
  //
  // === Fields (product-form/fields.js) ===
  // - updateDescriptionCount(event)
  // - showAiCopyHint()
  // - toggleFreeMode(event)
  // - syncFreeToggle()
  // - updateNegotiable(event)
  // - initializeNegotiableChip()
  // - syncTradeOptions()
  //
  // === Location (product-form/location.js) ===
  // - reverseGeocode(latitude, longitude)
  // - useCurrentLocation(event)
  // - openMapPicker()
  // - syncLocationDisplay()
  // - updateLocationStatus(message, success)
  // - handleMapCoordinateChange(event)
  // - handleAddressResolved(event)
  // - handleAutocompleteSelection(event)
  // - applyLocation(latitude, longitude, addressOverride)
}
